// Preact
import { h } from 'preact';
import { useEffect, useMemo, useRef } from 'preact/hooks';
import PropTypes from 'prop-types';

// Common
import Button from 'common/components/button';

/**
 * Marker component for displaying a map marker.
 *
 * @param {object} props - The properties of the Marker component.
 * @param {string} props.id - The unique identifier for the marker.
 * @param {Array} [props.datasets] - An array of additional datasets to attach to the marker.
 * @param {Array} props.coordinates - The coordinates [latitude, longitude] of the marker.
 * @param {string} [props.className] - Additional CSS class names for styling.
 * @param {h} [props.icon] - The JSX element representing the marker icon.
 * @param {string} [props.content] - The content to be displayed alongside the marker.
 * @param {string} [props.href] - The hyperlink for the marker.
 * @param {Function} [props.onClick] - The function to be called when the marker is clicked.
 *
 * @returns {h} The rendered Marker component.
 */
export function Marker({
    id,
    datasets = [],
    coordinates,
    className,
    icon,
    content,
    href,
    onClick
}) {
    const markerRef = useRef(null);

    const attributes = useMemo(
        () => [
            ...datasets,
            { title: 'data-pin-id', value: id },
            { title: 'data-coords', value: coordinates }
        ],
        [coordinates, datasets, id]
    );

    useEffect(() => {
        markerRef?.current?.setAttribute('data-coords', coordinates);
        markerRef?.current?.setAttribute('data-pin-id', id);

        attributes.forEach((dataset) =>
            markerRef?.current?.setAttribute(dataset.title, dataset.value)
        );
    }, [attributes, coordinates, id]);

    return (
        <Button
            ref={markerRef}
            className={className}
            href={href}
            onClick={onClick}
        >
            {icon}
            <span className="marker-text">{content}</span>
        </Button>
    );
}

// Prop Types Validation
Marker.propTypes = {
    id: PropTypes.number,
    datasets: PropTypes.arrayOf(
        PropTypes.shape({
            title: PropTypes.string.isRequired,
            value: PropTypes.any.isRequired
        })
    ),
    coordinates: PropTypes.arrayOf(PropTypes.number).isRequired,
    className: PropTypes.string,
    icon: PropTypes.element,
    content: PropTypes.string,
    href: PropTypes.string,
    onClick: PropTypes.func
};
